// AccountManagement.tsx
import React, { useEffect, useState, useRef, useCallback } from "react";
import styles from "./AccountManagement.module.css";
import WebService from "../Services/WebService";
import RecordPaymentModal from "./RecordPaymentModal";
import CustomerPanel from "./CustomerPanel";
import OutstandingBalances from "./OutstandingBalances";
import { AccountSection, parseAccountSection } from "./accountSection";
import { fetchOutstandingBalances } from "../Services/ledger";
import { OutstandingBalances as OutstandingBalancesData } from "../Models/Models";
import { FaFileAlt, FaCheckCircle, FaExclamationCircle, FaMoneyBillWave } from "react-icons/fa";
import { useNavigate, useSearchParams } from "react-router-dom";
import { navigateToBlade, usePathBlade } from "../Customs/FullWidthBlade/FullWidthBlade";

const PAGE_SIZE = 10;

function cleanLabel(value: unknown): string {
  if (value == null) return "";
  const s = String(value).trim();
  if (!s || s.toLowerCase() === "null" || s.toLowerCase() === "undefined") return "";
  return s;
}

function displayCustomerName(customer: any): string {
  const company = cleanLabel(customer?.company_name);
  const name = cleanLabel(customer?.customer_name ?? customer?.username);
  if (company && name) return `${company} (${name})`;
  return company || name || "-";
}

function formatMoney(value: number | string | null | undefined): string {
  if (value === null || value === undefined || value === "") return "₹0.00";
  const num = typeof value === "number" ? value : Number(value);
  if (Number.isNaN(num)) return "₹0.00";
  return `₹${num.toLocaleString("en-IN", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })}`;
}

type SummaryData = {
  total_orders?: number | string;
  total_pending_amount?: number | string;
  overdue_orders?: number | string;
  partial_pay_orders?: number | string;
};

const datePresets = [
  { label: "All", value: "all" },
  { label: "Today", value: "today" },
  { label: "Last Week", value: "last_week" },
  { label: "Last Month", value: "last_month" },
  { label: "Last 3 Months", value: "last_3_months" },
  { label: "Custom", value: "custom" }
];

function getPresetDates(preset: string) {
  const today = new Date();
  let from = "";
  let to = "";

  if (preset === "today") {
    from = to = today.toISOString().slice(0, 10);
  } else if (preset === "last_week") {
    const lastWeek = new Date(today);
    lastWeek.setDate(today.getDate() - 7);
    from = lastWeek.toISOString().slice(0, 10);
    to = today.toISOString().slice(0, 10);
  } else if (preset === "last_month") {
    const lastMonth = new Date(today);
    lastMonth.setMonth(today.getMonth() - 1);
    from = lastMonth.toISOString().slice(0, 10);
    to = today.toISOString().slice(0, 10);
  } else if (preset === "last_3_months") {
    const last3Months = new Date(today);
    last3Months.setMonth(today.getMonth() - 3);
    from = last3Months.toISOString().slice(0, 10);
    to = today.toISOString().slice(0, 10);
  } else if (preset === "all") {
    from = "";
    to = "";
  }
  return { from, to };
}

const AccountManagement: React.FC = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const [search, setSearch] = useState(searchParams.get("search") || "");
  const [page, setPage] = useState(1); // local only
  const [datePreset, setDatePreset] = useState(searchParams.get("preset") || "all");
  const [fromDate, setFromDate] = useState(searchParams.get("from") || "");
  const [toDate, setToDate] = useState(searchParams.get("to") || "");
  const [customers, setCustomers] = useState<any[]>([]);
  const [loading, setLoading] = useState(false);
  const [, setTotalPages] = useState(1); 
  const [paymentModalOpen, setPaymentModalOpen] = useState(false);
  const [addPanelOpen, setAddPanelOpen] = useState(false);
  const [summary, setSummary] = useState<SummaryData>({
    total_orders: "-",
    total_pending_amount: "-",
    overdue_orders: "-",
    partial_pay_orders: "-",
  });
  const [summaryError, setSummaryError] = useState(false);
  const [section, setSection] = useState<AccountSection>(
    () => parseAccountSection(searchParams.get("tab"))
  );
  const [outstandingRefreshKey, setOutstandingRefreshKey] = useState(0);
  const [outstanding, setOutstanding] = useState<OutstandingBalancesData | null>(null);
  const [outstandingLoading, setOutstandingLoading] = useState(false);
  const [outstandingError, setOutstandingError] = useState(false);

  const [hasMore, setHasMore] = useState(true); 
  const sentinelRef = useRef<HTMLDivElement | null>(null);
  const searchRef = useRef<NodeJS.Timeout | null>(null);
  const navigate = useNavigate();
  const bladeOpen = usePathBlade("/accounts-management/customer/:userId");
  const wasBladeOpen = useRef(false);

  useEffect(() => {
    if (wasBladeOpen.current && !bladeOpen) {
      setOutstandingRefreshKey((k) => k + 1);
    }
    wasBladeOpen.current = bladeOpen;
  }, [bladeOpen]);


  useEffect(() => {
    const params: Record<string, string> = {};
    if (section !== AccountSection.CUSTOMERS) params.tab = section;
    if (search) params.search = search;
    if (datePreset && datePreset !== "all") params.preset = datePreset;
    if (fromDate) params.from = fromDate;
    if (toDate) params.to = toDate;
    const view = searchParams.get("view");
    if (view) params.view = view;
    setSearchParams(params, { replace: true });
    // eslint-disable-next-line
  }, [search, datePreset, fromDate, toDate, section]);


  useEffect(() => {
    const paramSearch = searchParams.get("search") || "";
    if (paramSearch !== search) setSearch(paramSearch);

    const paramPreset = searchParams.get("preset") || "all";
    if (paramPreset !== datePreset) setDatePreset(paramPreset);

    const paramFrom = searchParams.get("from") || "";
    if (paramFrom !== fromDate) setFromDate(paramFrom);

    const paramTo = searchParams.get("to") || "";
    if (paramTo !== toDate) setToDate(paramTo);

    const paramSection = parseAccountSection(searchParams.get("tab"));
    if (paramSection !== section) setSection(paramSection);
    // eslint-disable-next-line
  }, [searchParams]);


  useEffect(() => {
    if (datePreset === "custom") return;
    const { from, to } = getPresetDates(datePreset);
    setFromDate(from);
    setToDate(to);
  }, [datePreset]);


  useEffect(() => {

    if (datePreset === "all") {
      const fetchSummary = async () => {
        try {
          const res = await WebService.getAPI<any>({
            action: `/v1/door-management/accounts/dashboard/data`
          });
          const d = res?.data;
          setSummary({
            total_orders: typeof d?.total_orders === "number" ? d.total_orders : "-",
            total_pending_amount: typeof d?.total_pending_amount === "number" ? d.total_pending_amount : "-",
            overdue_orders: typeof d?.overdue_orders === "number" ? d.overdue_orders : "-",
            partial_pay_orders: typeof d?.partial_pay_orders === "number" ? d.partial_pay_orders : "-",
          });
          setSummaryError(false);
        } catch {
          setSummary({
            total_orders: "-",
            total_pending_amount: "-",
            overdue_orders: "-",
            partial_pay_orders: "-",
          });
          setSummaryError(true);
        }
      };
      fetchSummary();
      return;
    }
    // Else, date preset is NOT all: use /dashboard/data with dates
    if (!fromDate || !toDate) {
      setSummary({
        total_orders: "-",
        total_pending_amount: "-",
        overdue_orders: "-",
        partial_pay_orders: "-",
      });
      setSummaryError(false);
      return;
    }
    const fetchSummary = async () => {
      try {
        const res = await WebService.getAPI<any>({
          action: `/v1/door-management/accounts/dashboard/data?from_date=${fromDate}&to_date=${toDate}`,
        });
        const d = res?.data;
        setSummary({
          total_orders: typeof d?.total_orders === "number" ? d.total_orders : "-",
          total_pending_amount: typeof d?.total_pending_amount === "number" ? d.total_pending_amount : "-",
          overdue_orders: typeof d?.overdue_orders === "number" ? d.overdue_orders : "-",
          partial_pay_orders: typeof d?.partial_pay_orders === "number" ? d.partial_pay_orders : "-",
        });
        setSummaryError(false);
      } catch {
        setSummary({
          total_orders: "-",
          total_pending_amount: "-",
          overdue_orders: "-",
          partial_pay_orders: "-",
        });
        setSummaryError(true);
      }
    };
    fetchSummary();
  }, [datePreset, fromDate, toDate]);

  useEffect(() => {
    setOutstandingLoading(true);
    setOutstandingError(false);
    fetchOutstandingBalances()
      .then((res: any) => {
        setOutstanding(res?.data || null);
      })
      .catch(() => {
        setOutstanding(null);
        setOutstandingError(true);
      })
      .finally(() => setOutstandingLoading(false));
  }, [outstandingRefreshKey]);

  const fetchCustomers = useCallback(
    (customer_like = "", page_number = 1, from_date = "", to_date = "", preset = "", append = false) => {
      setLoading(true);
      let action = `/v1/door-management/accounts?limit=${PAGE_SIZE}&page_number=${page_number}&customer_like=${encodeURIComponent(customer_like)}`;
      // Only add dates if not "all"
      if (preset !== "all") {
        if (from_date) action += `&from_date=${from_date}`;
        if (to_date) action += `&to_date=${to_date}`;
      }
      WebService.getAPI({
        action,
      })
        .then((res: any) => {
          const list = res.data?.accounts || [];
          const pg = res.data?.pagination;
          setCustomers(prev => (append ? [...prev, ...list] : list));
          setTotalPages(pg?.total_pages || 1);

          if (pg?.current_page != null && pg?.total_pages != null) {
            setHasMore(pg.current_page < pg.total_pages);
          } else {
            setHasMore(list.length >= PAGE_SIZE);
          }
        })
        .catch(() => {
          if (!append) {
            setCustomers([]);
            setTotalPages(1);
          }
          setHasMore(false);
        })
        .finally(() => setLoading(false));
    }, []
  );


  useEffect(() => {
    setPage(1);
    setHasMore(true);
    setCustomers([]);
    fetchCustomers(search, 1, fromDate, toDate, datePreset, false);
  }, [fetchCustomers, fromDate, toDate, datePreset, search]);


  const handleSearch = (e: React.ChangeEvent<HTMLInputElement>) => {
    const val = e.target.value;
    setPage(1);
    setHasMore(true);
    setCustomers([]);
    setSearch(val);
    if (searchRef.current) clearTimeout(searchRef.current);
    searchRef.current = setTimeout(() => {
      fetchCustomers(val, 1, fromDate, toDate, datePreset, false);
    }, 350);
  };


  const handleFromDateChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setFromDate(e.target.value);
    setPage(1);
    setHasMore(true);
    setDatePreset("custom");
  };
  const handleToDateChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setToDate(e.target.value);
    setPage(1);
    setHasMore(true);
    setDatePreset("custom");
  };


  useEffect(() => {
    if (page > 1) {
      fetchCustomers(search, page, fromDate, toDate, datePreset, true);
    }
  }, [page, fetchCustomers, search, fromDate, toDate, datePreset]);


  useEffect(() => {
    const node = sentinelRef.current;
    if (!node || bladeOpen || section !== AccountSection.CUSTOMERS) return;

    const obs = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting && hasMore && !loading) {
          setPage(p => p + 1);
        }
      },
      { root: null, rootMargin: "200px", threshold: 0 }
    );

    obs.observe(node);
    return () => {
      obs.disconnect();
    };
  }, [hasMore, loading, bladeOpen, section]);

  // Record Payment Modal: always global
  const openRecordPayment = () => setPaymentModalOpen(true);

  const handleViewAll = (user_id: number) => {
    const params = new URLSearchParams(searchParams);
    params.set("view", "ledger");
    navigateToBlade(navigate, `/accounts-management/customer/${user_id}`, params);
  };
  const handleOpenVendor = (vendorId: number) => {
    navigate(`/vendor-management/${vendorId}`);
  };
  const CARDS = [
    {
      label: "Total Orders",
      value: summary.total_orders ?? "-",
      icon: <FaFileAlt />,
      iconBg: styles.blueBg,
    },
    {
      label: "Total Pending Amount",
      value:
        summary.total_pending_amount !== undefined &&
        summary.total_pending_amount !== "-"
          ? `₹${summary.total_pending_amount}`
          : "-",
      icon: <FaCheckCircle />,
      iconBg: styles.greenBg,
    },
    {
      label: "Overdue Orders",
      value: summary.overdue_orders ?? "-",
      icon: <FaExclamationCircle />,
      iconBg: styles.redBg,
    },
    {
      label: "Partial Payments",
      value: summary.partial_pay_orders ?? "-",
      icon: <FaMoneyBillWave />,
      iconBg: styles.purpleBg,
    },
  ];
  return (
    <div className={styles.pageWrapper}>
      <div inert={bladeOpen ? true : undefined} aria-hidden={bladeOpen || undefined}>
      {/* Header and Controls */}
      <div className={styles.headerRow}>
        <div>
          <h1 className={styles.pageTitle}>Accounts</h1>
          <p className={styles.pageSubtitle}>
            {section === AccountSection.CUSTOMERS && "All customer accounts and ledger details"}
            {section === AccountSection.DEBTORS && "Customers with outstanding receivables"}
            {section === AccountSection.CREDITORS && "Vendors with outstanding payables"}
          </p>
        </div>
      </div>

      <div className={styles.tabs} role="tablist" aria-label="Account sections">
        {([
          { id: AccountSection.CUSTOMERS, label: "Customers" },
          { id: AccountSection.DEBTORS, label: "Debtors", count: outstanding?.debtor_count },
          { id: AccountSection.CREDITORS, label: "Creditors", count: outstanding?.creditor_count },
        ] as const).map((tab) => (
          <button
            key={tab.id}
            type="button"
            role="tab"
            aria-selected={section === tab.id}
            className={`${styles.tabBtn} ${section === tab.id ? styles.tabBtnActive : ""}`}
            onClick={() => setSection(tab.id)}
          >
            {tab.label}
            {"count" in tab && tab.count != null && (
              <span className={styles.tabCount}>{tab.count}</span>
            )}
          </button>
        ))}
      </div>

      {section === AccountSection.CUSTOMERS && (
        <>
      <div className={styles.filterBar}>
        <div className={styles.filterGroup}>
          <input
            className={styles.searchInput}
            placeholder="Search customer..."
            value={search}
            onChange={handleSearch}
          />
          <select
            className={styles.datePresetDropdown}
            value={datePreset}
            onChange={e => setDatePreset(e.target.value)}
          >
            {datePresets.map((preset) => (
              <option key={preset.value} value={preset.value}>
                {preset.label}
              </option>
            ))}
          </select>
          <div className={styles.dateFilters}>
            {/* From date */}
            <input
              type="date"
              value={fromDate}
              onChange={handleFromDateChange}
              className={styles.dateInput}
              placeholder="From date"
              max={datePreset === "custom" ? toDate || undefined : undefined}
            />
            {/* To date (only in custom) */}
            {datePreset === "custom" && (
              <input
                type="date"
                value={toDate}
                onChange={handleToDateChange}
                className={styles.dateInput}
                placeholder="To date"
                min={fromDate || undefined}
              />
            )}
          </div>
        </div>
        <div className={styles.buttonGroup}>
          <button
            className={styles.recordPaymentBtn}
            onClick={() => setAddPanelOpen(true)}
            type="button"
          >
            + Add Customer
          </button>
          <button
            className={styles.recordPaymentBtn}
            onClick={openRecordPayment}
            type="button"
          >
            + Record Payment
          </button>
        </div>
      </div>
      {/* Summary Cards */}
      <div className={styles.cardsRow}>
        {CARDS.map((card) => (
          <div className={styles.summaryCard} key={card.label}>
            <div className={`${styles.iconCircle} ${card.iconBg}`}>{card.icon}</div>
            <div className={styles.cardContent}>
              <div className={styles.cardLabel}>{card.label}</div>
              <div className={styles.cardValue}>{card.value}</div>
            </div>
          </div>
        ))}
      </div>
      {summaryError && (
        <div style={{ color: "#e43e2d", margin: "10px 0 0 10px" }}>
          Failed to load summary data.
        </div>
      )}
      {/* Customers Table */}
      <div className={styles.customersTableWrapper}>
        <table className={styles.customersTable}>
          <thead>
            <tr>
              <th className={styles.nameCell}>Customer</th>
              <th>Customer ID</th>
              <th>Orders</th>
              <th>Due Amount</th>
              <th>Action</th>
            </tr>
          </thead>
          <tbody>
            {loading && customers.length === 0 ? (
              <tr>
                <td colSpan={5} className={styles.noResults}>
                  Loading...
                </td>
              </tr>
            ) : customers.length === 0 ? (
              <tr>
                <td colSpan={5} className={styles.noResults}>
                  No customers found.
                </td>
              </tr>
            ) : (
              <>
                {customers.map((customer) => {
                  const due = customer.overall_due_amount ?? customer.outstanding ?? customer.due_amount;
                  const dueNum = Number(due);
                  const settled = due != null && due !== "" && !Number.isNaN(dueNum) && dueNum === 0;
                  return (
                  <tr key={customer.customer_id}>
                    <td className={styles.nameCell}>
                      <div className={styles.partyName}>{displayCustomerName(customer)}</div>
                      {cleanLabel(customer.mobile_number) && (
                        <div className={styles.partyMeta}>{customer.mobile_number}</div>
                      )}
                    </td>
                    <td>{customer.customer_id}</td>
                    <td>{customer.total_orders ?? "-"}</td>
                    <td>
                      <span className={settled ? styles.settledBadge : styles.dueBadge}>
                        {formatMoney(due)}
                      </span>
                    </td>
                    <td>
                      <button
                        className={styles.viewAllBtn}
                        onClick={() => handleViewAll(customer.user_id)}
                      >
                        View Ledger
                      </button>
                    </td>
                  </tr>
                  );
                })}
                {/* Sentinel row for infinite (invisible) pagination */}
                <tr>
                  <td colSpan={5}>
                    <div ref={sentinelRef} style={{ height: 1 }} />
                    <div style={{ textAlign: "center", padding: 12, color: "#666" }}>
                      {loading ? "Loading…" : hasMore ? "Loading more…" : "— End —"}
                    </div>
                  </td>
                </tr>
              </>
            )}
          </tbody>
        </table>
      </div>
        </>
      )}

      {section === AccountSection.DEBTORS && (
        <OutstandingBalances
          section={AccountSection.DEBTORS}
          data={outstanding}
          loading={outstandingLoading}
          error={outstandingError}
          onOpenCustomer={handleViewAll}
          onOpenVendor={handleOpenVendor}
          headerAction={
            <button
              className={styles.recordPaymentBtn}
              onClick={openRecordPayment}
              type="button"
            >
              + Record Payment
            </button>
          }
        />
      )}

      {section === AccountSection.CREDITORS && (
        <OutstandingBalances
          section={AccountSection.CREDITORS}
          data={outstanding}
          loading={outstandingLoading}
          error={outstandingError}
          onOpenCustomer={handleViewAll}
          onOpenVendor={handleOpenVendor}
        />
      )}
      {/* Record Payment Modal*/}
      {paymentModalOpen && (
        <RecordPaymentModal
          open={paymentModalOpen}
          onClose={() => setPaymentModalOpen(false)}
          onSuccess={() => {
            setPaymentModalOpen(false);
            setPage(1);
            setHasMore(true);
            setCustomers([]);
            fetchCustomers(search, 1, fromDate, toDate, datePreset, false);
            setOutstandingRefreshKey((k) => k + 1);
            // Refresh dashboard summary cards after payment
            const action =
              datePreset === "all"
                ? `/v1/door-management/accounts/dashboard/data`
                : fromDate && toDate
                ? `/v1/door-management/accounts/dashboard/data?from_date=${fromDate}&to_date=${toDate}`
                : null;
            if (!action) return;
            WebService.getAPI<any>({ action })
              .then((res) => {
                const d = res?.data;
                setSummary({
                  total_orders: typeof d?.total_orders === "number" ? d.total_orders : "-",
                  total_pending_amount:
                    typeof d?.total_pending_amount === "number" ? d.total_pending_amount : "-",
                  overdue_orders: typeof d?.overdue_orders === "number" ? d.overdue_orders : "-",
                  partial_pay_orders:
                    typeof d?.partial_pay_orders === "number" ? d.partial_pay_orders : "-",
                });
                setSummaryError(false);
              })
              .catch(() => setSummaryError(true));
          }}
        />
      )}
      {/* ADD CUSTOMER PANEL */}
      {addPanelOpen && (
        <CustomerPanel
          open={addPanelOpen}
          mode="add"
          onClose={() => setAddPanelOpen(false)}
          onSaved={() => {
            setAddPanelOpen(false);
            // refresh from first page to keep consistent
            setPage(1);
            setHasMore(true);
            setCustomers([]);
            fetchCustomers(search, 1, fromDate, toDate, datePreset, false);
          }}
        />
      )}
      </div>
    </div>
  );
};
export default AccountManagement;
